import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Sparkles, ArrowRight, School, ArrowLeft, GraduationCap } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1260&q=80';

export default function StudentLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(1);
  const [schoolCode, setSchoolCode] = useState(searchParams.get('code') || '');
  const [schoolData, setSchoolData] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [codeError, setCodeError] = useState('');

  const [admissionNumber, setAdmissionNumber] = useState('');
  const [dob, setDob] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get('code')) handleVerifyCode(searchParams.get('code'));
  }, []);

  const handleVerifyCode = async (code) => {
    const cleaned = (code || schoolCode).trim().toUpperCase();
    if (!cleaned || cleaned.length < 3) { setCodeError('Please enter a valid school code'); return; }
    setVerifying(true);
    setCodeError('');
    try {
      const res = await axios.get(`${API_URL}/auth/verify-school-code/${cleaned}`);
      setSchoolData(res.data.data);
      setSchoolCode(cleaned);
      setStep(2);
    } catch {
      setCodeError('School code not found. Check with your school.');
    } finally {
      setVerifying(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!admissionNumber.trim() || !dob) { toast.error('All fields required'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/student/login`, {
        admission_number: admissionNumber.trim(),
        date_of_birth: dob,
        school_code: schoolCode,
      });
      const data = res.data.data;
      localStorage.setItem('student_token', data.access_token);
      localStorage.setItem('student_user', JSON.stringify(data.user));
      toast.success(`Welcome, ${data.user.full_name}!`);
      navigate('/student/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="student-login-page">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src={DEFAULT_IMAGE} alt="Students" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="flex items-center gap-2 mb-6">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-md">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-bold text-white">Ariskool</span>
          </div>
          <h1 className="text-3xl font-bold text-white leading-tight">Student Portal</h1>
          <p className="text-slate-300 mt-2 text-base">View your exam results, fee status, and school announcements</p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-950">
        <div className="w-full max-w-md">
          {step === 1 ? (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white">Student Login</h2>
                <p className="text-slate-400 text-sm mt-1">Enter your school code to continue</p>
              </div>
              <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800">
                <Label className="text-xs text-slate-400">School Code</Label>
                <div className="flex gap-2 mt-1">
                  <div className="relative flex-1">
                    <School className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input value={schoolCode} onChange={e => { setSchoolCode(e.target.value.toUpperCase()); setCodeError(''); }}
                      placeholder="E.G. DPS-2847" className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                      onKeyDown={e => e.key === 'Enter' && handleVerifyCode()}
                      data-testid="student-school-code-input" />
                  </div>
                </div>
                {codeError && <p className="text-red-400 text-xs mt-2">{codeError}</p>}
                <Button onClick={() => handleVerifyCode()} disabled={verifying} className="w-full mt-4 bg-teal-500 hover:bg-teal-600 text-white" data-testid="student-verify-code-btn">
                  {verifying ? 'Verifying...' : 'Continue'} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
              <p className="text-center text-slate-500 text-sm">
                Parent? <Link to="/parent/login" className="text-teal-400 font-semibold hover:underline">Parent Portal</Link>
              </p>
              <p className="text-center text-slate-500 text-sm">
                Staff? <Link to="/login" className="text-teal-400 font-semibold hover:underline">Staff Login</Link>
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <button onClick={() => setStep(1)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white transition-colors" data-testid="student-back-btn">
                <ArrowLeft className="h-4 w-4" /> Change school
              </button>
              {schoolData && (
                <div className="flex items-center gap-3 bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <div className="h-10 w-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                    <GraduationCap className="h-5 w-5 text-teal-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{schoolData.school_name}</p>
                    <p className="text-xs text-slate-400">{schoolCode}</p>
                  </div>
                </div>
              )}
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-400">Admission Number</Label>
                  <Input value={admissionNumber} onChange={e => setAdmissionNumber(e.target.value.toUpperCase())} placeholder="e.g., ADM-D4F2F8"
                    className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" required data-testid="student-admission-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Date of Birth</Label>
                  <Input type="date" value={dob} onChange={e => setDob(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white" required data-testid="student-dob-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-teal-500 hover:bg-teal-600 text-white" data-testid="student-login-btn">
                  {loading ? 'Signing in...' : 'Sign in'} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
